import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';
import JsonLd from '../../components/base/JsonLd';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'About',
        item: `${siteUrl}/about`
      }
    ]
  };

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Diivy',
    url: `${siteUrl}/about`,
    description: 'Learn about Diivy\'s approach to building and supporting focused companies in health technology, safety, and commerce. We combine strategic positioning, operational support, and long-term commitment to create lasting value.',
    mainEntity: {
      '@type': 'Organization',
      name: 'Diivy Medical Technology',
      url: siteUrl,
      description: 'Diivy Medical Technology is a holding company focused on applied innovation across healthcare, veterinary, and RFID-enabled industrial markets.',
      foundingDate: '2020',
      mission: 'To support and develop technology platforms that improve how critical industries operate — with a focus on safety, visibility, and long-term value creation.',
      knowsAbout: [
        'Health Technology',
        'Medical Technology',
        'Safety Technology',
        'Veterinary Care',
        'RFID Solutions',
        'Industrial Tracking'
      ]
    }
  };

  return (
    <>
      <SEOHead
        title="About Diivy | Operating Mindset & Long-Term Value Creation"
        description="Learn about Diivy's approach to building and supporting focused companies in health technology, safety, and commerce. We combine strategic positioning, operational support, and long-term commitment to create lasting value."
        keywords="about Diivy, holding company philosophy, operating mindset, long-term value creation, company building, strategic positioning"
        canonical="/about"
        ogTitle="About Diivy | Operating Mindset & Long-Term Value Creation"
        ogDescription="Diivy builds and supports focused companies where thoughtful execution and long-term commitment matter. Learn about our approach to value creation."
        ogUrl="/about"
        twitterTitle="About Diivy | Operating Mindset & Long-Term Value Creation"
        twitterDescription="Diivy builds and supports focused companies where thoughtful execution and long-term commitment matter."
      />
      <JsonLd data={aboutPageSchema} />
      <JsonLd data={breadcrumbSchema} />
      
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero - Editorial Header */}
        <section className="pt-48 pb-32 bg-white">
          <div className="max-w-[1080px] mx-auto px-12">
            {/* Gold eyebrow label */}
            <div className="flex items-center gap-4 mb-10">
              <span className="block w-12 h-[1px] bg-[#C9A96E]"></span>
              <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                About Diivy
              </p>
            </div>

            {/* Large editorial headline with mixed weight */}
            <h1 className="text-6xl lg:text-7xl font-light text-[#0A0F1E] mb-12 leading-[1.1] tracking-[-0.02em]">
              Building <span className="font-semibold">Applied Technology</span><br />
              for Critical Industries
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed max-w-[720px] font-light">
              Diivy Medical Technology is a holding company focused on applied innovation across healthcare, veterinary, and RFID-enabled industrial markets.
            </p>
          </div>
        </section>

        {/* About Body - Two Column with Pull Quote */}
        <section className="py-32 bg-[#F7F4EF]">
          <div className="max-w-[1280px] mx-auto px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-20">
              {/* Left: Editorial Body Text */}
              <div className="lg:col-span-7 space-y-8 text-lg text-gray-700 leading-[1.85] font-light">
                <p>
                  Diivy was established to build and support technology ventures with clear real-world applications. Its approach is selective and execution-oriented, centered on platforms that improve safety, strengthen operational visibility, and create durable value in important sectors.
                </p>
                <p>
                  The company's current areas of focus include passive monitoring, veterinary care infrastructure, and RFID-enabled systems for medical and industrial environments. Across each area, the common thread is practical deployment: technology that can be implemented, measured, and scaled.
                </p>
                <p>
                  Diivy operates with a long-term perspective, supporting ventures that address structural needs rather than transient opportunities. The portfolio is built around platforms with defensible positioning and clear paths to operational maturity.
                </p>
              </div>

              {/* Right: Large Pull Quote with Decorative Frame */}
              <div className="lg:col-span-5 flex items-center">
                <div className="relative">
                  {/* Decorative corner borders */}
                  <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-[#C9A96E]/40"></div>
                  <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-[#C9A96E]/40"></div>
                  
                  <div className="px-12 py-16">
                    <p className="text-2xl lg:text-3xl font-light text-[#0A0F1E] leading-[1.5] tracking-[-0.01em] italic">
                      "Technology that can be implemented, measured, and scaled."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision - Large Numbered Cards */}
        <section className="py-40 bg-white">
          <div className="max-w-[1320px] mx-auto px-12">
            {/* Section label */}
            <div className="flex items-center gap-4 mb-20">
              <span className="block w-12 h-[1px] bg-[#C9A96E]"></span>
              <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#C9A96E]">
                Foundation
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
              {/* Mission Card */}
              <div className="space-y-10 group">
                <div className="flex items-start justify-between">
                  <span className="text-[11px] font-semibold tracking-[0.2em] text-[#C9A96E]/60">01</span>
                  <div className="w-14 h-14 flex items-center justify-center border border-[#0A0F1E]/10 hover:border-[#C9A96E] hover:bg-[#C9A96E] transition-all duration-500 group cursor-default">
                    <i className="ri-compass-3-line text-xl text-[#0A0F1E]/40 group-hover:text-white transition-colors duration-500"></i>
                  </div>
                </div>
                
                {/* Gold divider line */}
                <div className="w-full h-[1px] bg-gradient-to-r from-[#C9A96E] to-transparent"></div>
                
                <h2 className="text-4xl font-semibold text-[#0A0F1E] tracking-[-0.015em]">
                  Mission
                </h2>
                
                <p className="text-lg text-gray-600 leading-[1.85] font-light">
                  To support and develop technology platforms that improve how critical industries operate — with a focus on safety, visibility, and long-term value creation.
                </p>
              </div>

              {/* Vision Card */}
              <div className="space-y-10 group">
                <div className="flex items-start justify-between">
                  <span className="text-[11px] font-semibold tracking-[0.2em] text-[#C9A96E]/60">02</span>
                  <div className="w-14 h-14 flex items-center justify-center border border-[#0A0F1E]/10 hover:border-[#C9A96E] hover:bg-[#C9A96E] transition-all duration-500 group cursor-default">
                    <i className="ri-eye-line text-xl text-[#0A0F1E]/40 group-hover:text-white transition-colors duration-500"></i>
                  </div>
                </div>
                
                {/* Gold divider line */}
                <div className="w-full h-[1px] bg-gradient-to-r from-[#C9A96E] to-transparent"></div>
                
                <h2 className="text-4xl font-semibold text-[#0A0F1E] tracking-[-0.015em]">
                  Vision
                </h2>
                
                <p className="text-lg text-gray-600 leading-[1.85] font-light">
                  To build a trusted portfolio of applied technology businesses with long-term strategic relevance and defensible market positioning.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Strip - Dark Background */}
        <section className="py-24 bg-[#0A0F1E] border-t border-white/5">
          <div className="max-w-[1320px] mx-auto px-12 flex flex-col lg:flex-row items-center justify-between gap-12">
            <div className="max-w-[640px]">
              <h3 className="text-3xl font-light text-white mb-6 leading-[1.4] tracking-[-0.01em]">
                Interested in Diivy's portfolio or sector focus?
              </h3>
              <p className="text-lg text-white/50 leading-relaxed font-light">
                We welcome conversations with institutional partners and investors.
              </p>
            </div>
            
            <Link
              to="/contact"
              className="px-10 py-4 bg-transparent border-2 border-[#C9A96E] text-[#C9A96E] text-[13px] font-semibold tracking-wide hover:bg-[#C9A96E] hover:text-[#0A0F1E] transition-all duration-400 whitespace-nowrap cursor-pointer inline-flex items-center gap-3 group"
            >
              Contact Diivy
              <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform duration-400"></i>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}